import { Box, Chip } from '@mui/material';
import { makeStyles, useTheme, withStyles } from '@mui/styles';
import clsx from 'clsx';
import { TicketDiscount } from 'iconsax-react';
import React from 'react';

import { Megaphone } from 'assets';
import ImageKit from 'components/common/Image';

const useStyles = makeStyles((theme) => ({
	info: {
		backgroundColor: `${theme.palette.info[400]} !important`,
		color: `${theme.palette.info.main} !important`,
		borderRadius: theme.shape.small,
		padding: '0 !important',
		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.info.main,
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: `0.1rem 0.4rem !important`,
			borderRadius: '0.4rem',
		},
	},
	error: {
		backgroundColor: `${theme.palette.error[400]} !important`,
		color: `${theme.palette.error.main} !important`,
		padding: '0.6rem 1rem',
		display: 'flex !important',
		flexDirection: 'row-reverse !important',
		borderRadius: theme.shape.small,
		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.error.main,
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: `0.1rem 0.4rem !important`,
			borderRadius: '0.4rem',
		},
	},
	warning: {
		backgroundColor: `${theme.palette.secondary[300]} !important`,
		color: `${theme.palette.secondary.light} !important`,
		borderColor: ` ${theme.palette.secondary.light} !important`,
		borderRadius: `${theme.shape.small}px !important`,
		display: 'flex',
		padding: '0.8rem 0.8rem !important',
		flexDirection: 'row-reverse',
		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.secondary.light,
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: '0.4rem 0.4rem !important',
			borderRadius: `8px !important`,
		},
	},
	success: {
		backgroundColor: `${theme.palette.success2[400]} !important`,
		color: `${theme.palette.success2.main} !important`,
		padding: '0.8rem 0.8rem !important',
		display: 'flex !important',
		flexDirection: 'row-reverse !important',
		alignItems: 'center',

		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.success2.main,
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: `0.1rem 0.4rem !important`,
			borderRadius: '0.4rem',
		},
	},
	online: {
		backgroundColor: `${theme.palette.success[300]} !important`,
		color: `${theme.palette.success.main} !important`,
		display: 'flex',
		flexDirection: 'reo-reverse',
		padding: '0.8rem 0.7rem !important',

		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.success.main,
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: `0.1rem 0.4rem !important`,
			borderRadius: '0.4rem',
		},
	},
	category: {
		backgroundColor: `${theme.palette.background.light} !important`,
		color: `${theme.palette.text.dark} !important`,
		borderColor: ` ${theme.palette.info[700]} !important`,
		boxShadow: `${theme.shadows[1]} !important`,
		borderRadius: `${theme.shape.medium}px !important`,
		padding: '2.3rem 1.6rem !important',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	filter: {
		color: `${theme.palette.primary[700]} !important`,
		backgroundColor: ` ${theme.palette.primary[200]} !important`,
		borderRadius: `0.8rem !important`,
		margin: '0.4rem',
		padding: '0.8rem 0.7rem !important',
		fontSize: '1.4rem',
		fontWeight: 500,
		lineHeight: '2.1rem',
		display: 'flex !important',
		flexDirection: 'row-reverse !important',
		alignItems: 'center',
		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.primary[700],
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: `0.1rem 0.4rem !important`,
			borderRadius: '0.8rem',
		},
	},
	primary: {
		color: `${theme.palette.primary[700]} !important`,
		backgroundColor: ` ${theme.palette.primary[200]} !important`,
		borderRadius: `0.8rem !important`,
		margin: '0.4rem',
		padding: '0.8rem 0.7rem !important',
		fontSize: '1.4rem',
		fontWeight: 500,
		lineHeight: '2.1rem',
		display: 'flex !important',
		flexDirection: 'row-reverse !important',
		alignItems: 'center',
		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.primary[700],
		},
		// [theme.breakpoints.between('xs', 'sm')]: {
		// 	padding: `0.1rem 0.4rem !important`,
		// 	borderRadius: '0.4rem !important',
		// },
	},
	soon: {
		background: `${theme.palette.primary[200]} !important`,
		color: `${theme.palette.text.dark} !important`,
		padding: '0.6rem 0.8rem',
		display: 'flex',
		flexDirection: 'row-reverse',
		borderRadius: theme.shape.small,
		'& svg': {
			fill: ({ iconColor }) => iconColor || theme.palette.text.dark,
		},
		[theme.breakpoints.between('xs', 'sm')]: {
			padding: `0.1rem 0.4rem !important`,
			borderRadius: '0.4rem',
		},
	},
	advertising: {
		padding: '0.2rem 0.8rem',
		backgroundColor: ` ${theme.palette.background.light} !important`,
		color: `${theme.palette.primary[700]} !important`,
		display: 'flex !important',
		flexDirection: 'row-reverse !important',
		alignItems: 'center',
		borderRadius: theme.shape.small,
		fontSize: '1.2rem !important',
		boxShadow: theme.shadows[1],
	},
	disabled: {
		backgroundColor: `${theme.palette.text.disabled} !important`,
		borderColor: `${theme.palette.text.disabled} !important`,
		color: `${theme.palette.text.primary} !important`,
		cursor: 'not-allowed !important',
		'& svg': {
			fill: `${theme.palette.text.disabled} !important`,
		},
	},
	iconColor: {
		'& svg': {
			fill: ({ iconColor }) => iconColor,
		},
	},
}));

const CustomChip = withStyles((theme) => ({
	root: {
		fontSize: theme.typography.subtitle1.fontSize,
		fontWeight: 500,
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	icon: {},
	outlined: {
		backgroundColor: `${theme.palette.common.white} !important`,
		borderColor: theme.palette.grey[300],
	},
}))(Chip);

const ChipKit = (props) => {
	const { colors, iconColor, disabled, label, ...rest } = props;
	const theme = useTheme();
	const classes = useStyles({ iconColor });

	const handleSwitchAvatar = React.useMemo(() => {
		switch (colors) {
			case 'Style1':
				return (
					<Box width={'1rem'} height={'1rem'} display="flex" flexDirection="row" alignItems={'center'}>
						<ImageKit src={Megaphone} />
					</Box>
				);

			case 'Style3':
			case 'Style4':
				return (
					<Box className="element-center" style={{ display: 'flex', flexDirection: '' }}>
						<TicketDiscount
							variant="Outline"
							color={colors === 'Style3' ? theme.palette.error.main : theme.palette.success2.main}
							size={rest.size === 'small' ? 18 : 20}
						/>
					</Box>
				);

			default:
				break;
		}
	}, [colors, rest.size, theme.palette.error.main, theme.palette.success2.main]);

	return (
		<CustomChip
			className={clsx({
				[classes.info]: ['Style6', 'info']?.includes(colors),
				[classes.success]: ['Style4', 'success']?.includes(colors),
				[classes.category]: colors?.includes('category'),
				[classes.filter]: colors?.includes('filter'),
				[classes.primary]: colors?.includes('primary'),
				[classes.soon]: ['Style2']?.includes(colors),
				[classes.warning]: ['warning']?.includes(colors),
				[classes.advertising]: ['Style1']?.includes(colors),
				[classes.online]: ['Style5']?.includes(colors),
				[classes.error]: ['Style3', 'error']?.includes(colors),
				[classes.iconColor]: iconColor,
				[classes.disabled]: disabled,
			})}
			disabled={disabled}
			size={'small'}
			label={label}
			avatar={handleSwitchAvatar}
			{...rest}
		/>
	);
};

ChipKit.defaultProps = {};

export default ChipKit;
